import { useEffect, useState } from "react";
import {
  Users,
  TrendingUp,
  UserCheck,
  UserMinus,
  Gift,
  Calendar,
  FileText,
  IdCard,
  CheckCircle,
  Clock,
  Paperclip,
  ChartPie,
} from "lucide-react";
import Skeleton from "@mui/material/Skeleton";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router";
import Style from "./style.module.css";
import WorkForceQuickView from "./popup";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { TableCard } from "../../../../component/table/table-layout";
import { CustomDropdown } from "../../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../../component/ui/dropdown/CustomDropdownMenuList";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import worldMapData from "@highcharts/map-collection/custom/world.geo.json";
import TopAccountList from "../../accounts/overview/topAccounts";
import NoInsights from "../../../../component/ui/loader/no-chart-data";
import { KPI } from "../../../../component/ui/layout";
import { CustomHighChart } from "../../../../component/ui/chart/custom-chart";
import { percentToNumber } from "../../../../component/form/form-layout";

const Card = ({ className = "", children }) => (
  <div
    className={`w-full border border-[#E1E4EA] shadow rounded-[14px] flex flex-col border-slate-200/60 1darkborder-slate-800 bg-white 1darkbg-slate-900 ${className}`}
  >
    {children}
  </div>
);
const CardHeader = ({ className = "", children }) => (
  <div
    className={`p-4 md:p-5 border-b border-slate-100 1darkborder-slate-800 ${className}`}
  >
    {children}
  </div>
);
const CardTitle = ({ className = "", children }) => (
  <h3
    className={`text-lg md:text-xl font-semibold tracking-tight ${className}`}
  >
    {children}
  </h3>
);
const CardContent = ({ className = "", children }) => (
  <div className={`p-4 md:p-5 w-full h-full ${className}`}>{children}</div>
);

// --- Main Component ---
export default function AccountsOverview() {
  // let data = "";
  const apiCall = useApiCall();
  const router = useNavigate();
  const [KPIData, setKPIData] = useState({});
  const [KPILoading, setKPILoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [selectedTeamSalesMonths, setSelectedTeamSalesMonths] = useState(6);

  const getKPIData = async () => {
    setKPILoading(true);
    try {
      const resp = await apiCall({
        method: "get",
        url: `accounts/accounts-overview-kpi?view=organization`,
      });
      setKPIData(resp?.data);
      setKPILoading(false);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    getKPIData();
    getTrendData();
    getTeamSalesData();
  }, []);

  // LINE CHART
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const getTrendData = async () => {
    setTrendLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: "accounts/accounts-overview-by-trend?view=organization",
      });
      setTrendData(res.data);
    } catch (error) {
      console.warn("Error fetching trend data:", error);
    } finally {
      setTrendLoading(true);
    }
  };

  const lineOptions = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 300,
    },

    title: { text: "" },

    xAxis: {
      categories: trendData?.slice(-selectedMonths)?.map((d) => d.month),
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },

    yAxis: {
      title: { text: "Accounts Count" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },

    legend: {
      align: "right",
      verticalAlign: "top",
    },

    series: [
      {
        name: "Assigned Accounts",
        data: trendData
          ?.slice(-selectedMonths)
          ?.map((d) => d.assigned_accounts),
        color: "#2563EB",
      },
      {
        name: "Total Transactions",
        data: trendData
          ?.slice(-selectedMonths)
          ?.map((d) => d.total_transaction),
        color: "#1FC16B",
      },
      {
        name: "Total Refund",
        data: trendData?.slice(-selectedMonths)?.map((d) => d.total_refund),
        color: "#F43F5E",
      },
    ],

    credits: { enabled: false },
  };

  // DONUT CHART

  // DONUT CHART - Leads by Source
  const [sourceData, setSourceData] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("this_month");
  const [allCount, setAllCount] = useState(false);

  const getLeadsBySourceData = async () => {
    setSourceLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: `accounts/accounts-overview-by-product?view=organization&time=${sourceFilter}`,
      });
      setSourceData(res?.data);
      const isAllZero = res?.data?.every((item) => Number(item.count) === 0);
      setAllCount(isAllZero);
    } catch (error) {
      console.warn("Error fetching leads by source:", error);
    } finally {
      setSourceLoading(true);
    }
  };

  useEffect(() => {
    getLeadsBySourceData();
  }, [sourceFilter]);

  const sourceOptions = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 320,
      spacing: [20, 0, 20, 0],
    },
    title: null,
    credits: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> leads<br/>({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        innerSize: "65%",
        borderWidth: 2,
        borderColor: "#fff",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: 15,
          style: { color: "#374151", fontSize: "12px" },
          format: "{point.name}: {point.percentage:.1f}%",
          // filter: { property: "percentage", operator: ">", value: 2 },
          filter: null,
        },
        showInLegend: true,
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { color: "#6B7280", fontSize: "12px" },
    },
    series: [
      {
        name: "Leads",
        colorByPoint: true, // disable auto colors
        data: sourceData?.map((el) => ({
          name: el?.product,
          y: el?.count,
        })),
      },
    ],
    events: {
      render() {
        const chart = this;
        const total = chart.series[0].data.reduce((acc, p) => acc + p.y, 0);
        const labelX = chart.plotWidth / 2 + chart.plotLeft - 25;
        const labelY = chart.plotHeight / 2 + chart.plotTop - 25;

        if (!chart.customLabel) {
          chart.customLabel = chart.renderer
            .label(`Total<br><b>${total}</b>`, labelX, labelY)
            .css({ textAlign: "center", color: "#111827", fontSize: "13px" })
            .add();
        } else {
          chart.customLabel.attr({ text: `Total<br><b>${total}</b>` });
        }
      },
    },
  };

  // PIE CHART - Leads by Brand
  const [brandData, setBrandData] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandFilter, setBrandFilter] = useState("this_month");
  const [allBrandsCount, setAllBrandsCount] = useState(false);

  const getLeadsByBrandData = async () => {
    setBrandLoading(false);
    const apiUrl = `accounts/accounts-overview-by-brand?view=organization&time=${brandFilter}`;
    try {
      const res = await apiCall({ method: "get", url: apiUrl });
      setBrandData(res?.data);
      const isAllZero = res?.data?.every((item) => Number(item.count) === 0);
      setAllBrandsCount(isAllZero);
    } catch (error) {
      console.warn("Error fetching brand data:", error);
    } finally {
      setBrandLoading(true);
    }
  };

  useEffect(() => {
    getLeadsByBrandData();
  }, [brandFilter]);

  const brandOptions = {
    chart: {
      type: "pie",
    },
    title: "none",
    credits: { enabled: false },
    tooltip: {
      pointFormat:
        "{series.name}: <b>{point.count}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format:
            "<b>{point.name}</b>: {point.percentage:.1f}% ({point.count})",
        },
      },
    },
    series: [
      {
        name: "Leads",
        colorByPoint: true,
        data: brandData?.map((brand) => ({
          count: brand?.count,
          name: brand?.brand,
          y: parseFloat(brand.percent),
        })),
      },
    ],
    legend: { enabled: false }, // single series, legend not needed
  };

  // Sales by Team

  // LINE CHART
  const [salesByTeam, setSalesByTeam] = useState([]);
  const [teamSalesLoading, setTeamSalesLoading] = useState(false);

  const getTeamSalesData = async () => {
    setTeamSalesLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: "accounts/accounts-overview-sales-by-team-trend?view=organization",
      });
      setSalesByTeam(res.data);
    } catch (error) {
      console.warn("Error fetching trend data:", error);
    } finally {
      setTeamSalesLoading(true);
    }
  };

  const months =
    salesByTeam[0]?.trend
      ?.slice(-selectedTeamSalesMonths)
      ?.map((item) => item.month) || [];

  const teamSalesOptions = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 350,
    },

    title: { text: "" },

    xAxis: {
      categories: months,
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },

    yAxis: {
      title: { text: "Total Sales" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },

    legend: { align: "right", verticalAlign: "top" },

    series: salesByTeam?.map((team, index) => ({
      name: team.team_name,
      data: team.trend
        ?.slice(-selectedTeamSalesMonths)
        ?.map((t) => t.total_sales),
      color: Highcharts.getOptions().colors[index], // auto-color
    })),

    credits: { enabled: false },
  };

  const [showWorkForce, setShowWorkForce] = useState(false);
  const [popupData, setPopupData] = useState({});

  return (
    <>
      <div className="pt-[20px] overflow-hidden">
        <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold">Organization Metrics</h1>
          </div>

          {/* KPI Row */}
          <TableCard className={"h-full overflow-hidden"}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {!KPILoading ? (
                <>
                  <KPI
                    icon={Users}
                    label="Total Accounts"
                    value={KPIData?.total_accounts}
                    sublabel={"Total"}
                    extraClass={"theme"}
                  />
                  <KPI
                    icon={UserCheck}
                    label="New Accounts"
                    value={KPIData?.today_accounts}
                    sublabel={`Today`}
                    // stateLable={`${KPIData?.today_percentage
                    //   ?.toFixed(0)
                    //   ?.replace("-", "")}%`}
                    stateLable={`${KPIData?.today_percentage}`}
                    positive={percentToNumber(KPIData?.today_percentage) > 0}
                  />
                  <KPI
                    icon={UserMinus}
                    label="New Accounts"
                    value={KPIData?.this_week_accounts}
                    sublabel={`This Week`}
                    stateLable={`${KPIData?.this_week_percentage}`}
                    positive={
                      percentToNumber(KPIData?.this_week_percentage) > 0
                    }
                  />
                  <KPI
                    icon={Gift}
                    label="New Accounts"
                    value={KPIData?.this_month_accounts}
                    sublabel={`This Month`}
                    stateLable={`${KPIData?.this_month_percentage}`}
                    positive={
                      percentToNumber(KPIData?.this_month_percentage) > 0
                    }
                  />

                  <KPI
                    icon={Calendar}
                    label="Assigned Accounts"
                    value={KPIData?.assigned_this_month}
                    sublabel={`Last 30 days`}
                    stateLable={`${KPIData?.assigned_percentage}`}
                    positive={percentToNumber(KPIData?.assigned_percentage) > 0}
                  />
                  <KPI
                    icon={Clock}
                    label="Unassigned Accounts"
                    value={KPIData?.unassigned_this_month}
                    sublabel={`Last 30 days`}
                    stateLable={`${KPIData?.unassigned_percentage}`}
                    positive={
                      percentToNumber(KPIData?.unassigned_percentage) > 0
                    }
                  />

                  <KPI
                    icon={CheckCircle}
                    label="Transaction Amount"
                    value={KPIData?.this_month_transactions_amount}
                    sublabel={`This Month`}
                    extraClass={"green"}
                    stateLable={`${KPIData?.this_month_transactions_amount_percentage}`}
                    positive={
                      percentToNumber(
                        KPIData?.this_month_transactions_amount_percentage,
                      ) > 0
                    }
                  />
                  <KPI
                    icon={IdCard}
                    label="Pending Deals Amount"
                    value={KPIData?.this_month_pending_deals_amount}
                    sublabel={`This Month`}
                    stateLable={`${KPIData?.this_month_pending_deals_amount_percentage}`}
                    positive={
                      percentToNumber(
                        KPIData?.this_month_pending_deals_amount_percentage,
                      ) > 0
                    }
                    extraClass={"yellow"}
                  />
                  <KPI
                    icon={FileText}
                    label="Refund Amount"
                    value={KPIData?.this_month_refund_amount}
                    sublabel={`This Month`}
                    stateLable={`${KPIData?.this_month_refund_amount_percentage}`}
                    positive={
                      percentToNumber(
                        KPIData?.this_month_refund_amount_percentage,
                      ) > 0
                    }
                    extraClass={"red"}
                  />
                  <KPI
                    icon={FileText}
                    label="Top Sales User"
                    value={KPIData?.top_sale_user?.name
                      ?.split(" ")
                      ?.slice(0, 2)
                      ?.join(" ")}
                    sublabel={`This Month`}
                    stateLable={`${KPIData?.top_sale_user?.total_sales_amount}`}
                    positive={
                      percentToNumber(
                        KPIData?.top_sale_user?.total_sales_amount,
                      ) > 0
                    }
                    positiveIcon={false}
                  />
                </>
              ) : (
                Array.from({ length: 10 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white shadow rounded-xl p-4 animate-pulse"
                  >
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1 mt-2 py-[12px]">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
              {/* Line Chart */}
              <Card className="col-span-3 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> By Accounts
                  </CardTitle>
                  <div
                    className={`flex items-center filterMonths ${Style.filterMonths}`}
                  >
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(6)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 6 ? Style.active : ""
                        }`}
                      ></div>
                      <p>6 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(9)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 9 ? Style.active : ""
                        }`}
                      ></div>
                      <p>9 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(12)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 12 ? Style.active : ""
                        }`}
                      ></div>
                      <p>12 Months</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* {trendLoading ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={lineOptions}
                    />
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart
                    options={lineOptions}
                    isLoad={trendLoading}
                    titleNotFound="No Data Found"
                  />
                </CardContent>
              </Card>

              {/* Doghnut Chart */}
              <Card className="col-span-2 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ChartPie className="h-5 w-5" /> By Brands
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {brandFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {brandFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}

                      {brandFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {/* {brandLoading ? (
                    brandData?.length > 0 && !allBrandsCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={brandOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart
                    options={brandOptions}
                    isLoad={brandLoading}
                  />
                </CardContent>
              </Card>

              {/* Pie Chart */}
              <Card className="col-span-2 min-h-[400px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Paperclip className="h-5 w-5" /> Accounts By Products
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {sourceFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {sourceFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}
                      {sourceFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}

                      {sourceFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {sourceFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}

                      {sourceFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {/* {sourceLoading ? (
                    sourceData?.length > 0 && !allCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={sourceOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart
                    options={sourceOptions}
                    isLoad={sourceLoading}
                  />
                </CardContent>
              </Card>

              {/* Top Accounts List */}

              <div className="col-span-1 md:col-span-3  p-5 min-h-[400px] max-h-[450px] bg-white border border-[#E1E4EA] shadow rounded-[10px] overflow-hidden">
                <TopAccountList />
              </div>

              {/* Sales By Team */}

              <Card className="col-span-5 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> Sales By Team
                  </CardTitle>
                  <div
                    className={`flex items-center filterMonths ${Style.filterMonths}`}
                  >
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedTeamSalesMonths(6)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedTeamSalesMonths == 6 ? Style.active : ""
                        }`}
                      ></div>
                      <p>6 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedTeamSalesMonths(9)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedTeamSalesMonths == 9 ? Style.active : ""
                        }`}
                      ></div>
                      <p>9 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedTeamSalesMonths(12)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedTeamSalesMonths == 12 ? Style.active : ""
                        }`}
                      ></div>
                      <p>12 Months</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* {teamSalesLoading ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={teamSalesOptions}
                    />
                  ) : (
                    <InnerLoader />
                  )} */}
                  <CustomHighChart
                    options={teamSalesOptions}
                    isLoad={teamSalesLoading}
                    titleNotFound="No Data Found"
                  />
                </CardContent>
              </Card>
            </div>
          </TableCard>
        </div>
      </div>

      {showWorkForce && (
        <WorkForceQuickView
          onClose={() => setShowWorkForce(false)}
          apiKey={popupData.apiKey}
          apiUrl={popupData.apiUrl}
          pagUrl={popupData.pagUrl}
          title={popupData.title}
          subTitle={popupData.subTitle}
          icon={popupData.icon}
          positive={popupData?.positive}
          stateLable={popupData?.stateLable}
          positiveIcon={popupData?.positiveIcon}
        />
      )}
    </>
  );
}
